const handleTokenExpiry = (data) => {
  if (data?.error === 'TOKEN_EXPIRED') {
    localStorage.removeItem('AUTH_TOKEN');
    window.location.href = '/';
    return true;
  }
  return false;
};

export const POST = (endpoint, requestBody = {}, authorization = null) => {
  const url = createRequestUrl(endpoint);
  const body = createRequestBody(requestBody);
  const headers = createRequestHeader(authorization);

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });
      if (response.status >= 400) {
        const data = await response.json();
        if (handleTokenExpiry(data)) return;
        reject(data);
      }
      const contentType = response?.headers.get('Content-Type');
      if (contentType?.includes('application/octet-stream')) {
        resolve(response.arrayBuffer());
      } else {
        const data = await response?.json();
        resolve(data);
      }
    } catch (err) {
      if (
        err.message &&
        err.message === 'NetworkError when attempting to fetch resource.'
      ) {
        err.message = 'Network Error. Please try again.';
      } else if (err.message && err.message === 'SERVER_ERROR') {
        err.message = 'Could not connect to server. Please try again.';
      }
      reject(err);
    }
  });
};

export const GET = (endpoint, authorization = null) => {
  const url = createRequestUrl(endpoint);
  const headers = createRequestHeader(authorization);

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });
      const data = await response.json();
      if (response.status >= 400) {
        if (handleTokenExpiry(data)) return;
        reject(data);
      }
      resolve(data);
    } catch (err) {
      if (
        err.message &&
        err.message === 'NetworkError when attempting to fetch resource.'
      ) {
        err.message = 'Network Error. Please try again.';
      } else if (err.message && err.message === 'SERVER_ERROR') {
        err.message = 'Could not connect to server. Please try again.';
      }
      reject(err);
    }
  });
};

export const PUT = (endpoint, requestBody = {}, authorization) => {
  const url = createRequestUrl(endpoint);
  const body = createRequestBody(requestBody);
  const headers = createRequestHeader(authorization);

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body,
      });
      const data = await response.json();
      if (response.status >= 400) {
        if (handleTokenExpiry(data)) return;
        reject(data);
      }
      resolve(data);
    } catch (err) {
      if (
        err.message &&
        err.message === 'NetworkError when attempting to fetch resource.'
      ) {
        err.message = 'Network Error. Please try again.';
      } else if (err.message && err.message === 'SERVER_ERROR') {
        err.message = 'Could not connect to server. Please try again.';
      }
      reject(err);
    }
  });
};

export const DELETE = (endpoint, requestBody = {}, authorization) => {

  const url = createRequestUrl(endpoint);
  const body = createRequestBody(requestBody);
  const headers = createRequestHeader(authorization);

  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
        body,
      });

      /* 
      Parse response based on Content-Type. 
      Some DELETE endpoints such as 'deleteProduct' return an text which would make response.json() throw. 
      Handle that gracefully 
      */

      const contentType = response.headers.get('Content-Type') || '';
      let data = null;

      if (contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType.includes('application/octet-stream')) {
        data = await response.arrayBuffer();
      } else {
        // Try to read as text; if empty, keep data as null
        const text = await response.text();
        data = text && text.length ? text : null;
      }

      if (response.status >= 400) {
        if (handleTokenExpiry(data)) return;
        reject(data);
      }

      resolve(data);
    } catch (err) {
      if (
        err.message &&
        err.message === 'NetworkError when attempting to fetch resource.'
      ) {
        err.message = 'Network Error. Please try again.';
      } else if (err.message && err.message === 'SERVER_ERROR') {
        err.message = 'Could not connect to server. Please try again.';
      }
      reject(err);
    }
  });
};

export const POST_FILES = (endpoint, uploadedFile, authorization = null) => {
  const url = createRequestUrl(endpoint);
  const headers = {
    // 'Content-Type': 'multipart/form-data',
    Authorization: `Bearer ${authorization}`,
  };

  return new Promise(async (resolve, reject) => {
    const formData = new FormData();
    formData.append('uploadFile', uploadedFile);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (response.status >= 400) {
        const data = await response.json();
        if (handleTokenExpiry(data)) return;
        reject(data);
      }
      const contentType = response.headers.get('Content-Type');
      if (contentType.includes('application/octet-stream')) {
        resolve(response.arrayBuffer());
      } else if (contentType.includes('application/json')) {
        const data = await response.json();
        resolve(data);
      } else {
        const data = await response.text();
        resolve(data);
      }
    } catch (err) {
      if (
        err.message &&
        err.message === 'NetworkError when attempting to fetch resource.'
      ) {
        err.message = 'Network Error. Please try again.';
      } else if (err.message && err.message === 'SERVER_ERROR') {
        err.message = 'Could not connect to server. Please try again.';
      }
      reject(err);
    }
  });
};

export const POST_FORM_DATA = (
  endpoint,
  requestBody,
  authorization = null,
  method = 'POST'
) => {
  const url = createRequestUrl(endpoint);
  const headers = {
    Authorization: `Bearer ${authorization}`,
  };

  return new Promise(async (resolve, reject) => {
    const formData = new FormData();

    // Append each key-value pair directly
    for (const key in requestBody) {
      formData.append(key, requestBody[key]);
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: formData,
      });

      if (response.status >= 400) {
        const errorData = await response.json();
        if (handleTokenExpiry(errorData)) return;
        console.error('Server Error:', errorData);
        reject(errorData);
      }

      const contentType = response.headers.get('Content-Type');
      if (
        contentType &&
        contentType.includes('application/octet-stream' || 'application/pdf')
      ) {
        resolve(response.blob());
      } else {
        const data = await response.json();
        resolve(data);
      }
    } catch (err) {
      reject(err);
    }
  });
};

export const POST_FILE_AND_JSON_DATA = (
  endpoint,
  requestBody,
  authorization = null,
  method = 'POST'
) => {
  const url = createRequestUrl(endpoint);
  const headers = {
    Authorization: `Bearer ${authorization}`,
  };

  return new Promise(async (resolve, reject) => {
    const formData = new FormData();

    for (const key in requestBody) {
      formData.append(
        key,
        new Blob([JSON.stringify(requestBody[key])], {
          type: 'application/json',
        }) // Ensure JSON format
      );
    }

    try {
      const response = await fetch(url, {
        method,
        headers, // Browser sets Content-Type automatically
        body: formData,
      });

      if (response.status >= 400) {
        const errorData = await response.json();
        if (handleTokenExpiry(errorData)) return;
        console.error('Server Error:', errorData);
        reject(errorData);
      }

      const contentType = response.headers.get('Content-Type');
      if (contentType.includes('application/json')) {
        const data = await response.json();
        resolve(data);
      } else {
        const data = await response.text();
        resolve(data);
      }
    } catch (err) {
      reject(err);
    }
  });
};

const createRequestUrl = (endpoint) => {
  return `${process.env.REACT_APP_MERCHANT_PORTAL_API}/v1.0/app${endpoint}`;
};

const createRequestBody = (requestBody) => {
  return JSON.stringify(requestBody);
};

const createRequestHeader = (authorization) => {
  return authorization
    ? {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authorization}`,
    }
    : {
      'Content-Type': 'application/json',
    };
};
